import { fetch_requestor_application_forms } from '@/services/requestor_portal_service';
import { getXVerdictFullConfigByEntityType } from '@/services/xverdict_entity_config_service';
import { fetch_assigned_questions_to_assessment, fetch_checklist_weights, fetch_submitted_risk_assessment_answers, submit_risk_assessment_answers } from '@/services/risk_service';
import { mapRiskBindingsToAssessments } from '@/services/xverdict_config_mapper';
import { assigned_risk_questions, entity_profile_id_from_application_forms, entity_type_id_from_application_forms, error_message, normalize_label, normalize_risk_panel, normalize_text, risk_choice_label, to_array, to_number } from './view_case_details_helpers';

export default {
  async resolve_risk_assessment() {
    const assessments = await this.resolve_risk_assessments();
    return assessments[0] || null;
  },
  async resolve_risk_assessment_questions(assessment = {}) {
    const assessment_id = to_number(assessment?.id);
    if (!assessment_id) {
      return [];
    }

    try {
      const [questions_payload, weights_payload] = await Promise.all([
        fetch_assigned_questions_to_assessment(assessment_id),
        fetch_checklist_weights(assessment_id).catch(empty_payload),
      ]);
      return assigned_risk_questions(questions_payload, weights_payload);
    } catch {
      return [];
    }
  },
  async with_risk_assessment_questions(assessment = {}) {
    return assessment_with_questions(
      assessment,
      await this.resolve_risk_assessment_questions(assessment)
    );
  },
  async resolve_case_backend_ids() {
    if (case_backend_ids_are_resolved(this)) {
      return;
    }

    await resolve_missing_backend_ids(this);
  },
  async resolve_risk_assessments() {
    const assessment_id = this.selected_risk_assessment_id;
    const assessments = await risk_assessments_for_context(this, assessment_id);
    return Promise.all(assessments.map((assessment) =>
      this.with_risk_assessment_questions(assessment)
    ));
  },
  async load_risk_panel() {
    await this.resolve_case_backend_ids();
    reset_risk_panel(this);

    if (!this.selected_entity_profile_id) {
      this.risk_panel_message = 'No backend risk score is available for this case because no entity profile id was supplied.';
      return;
    }

    await load_risk_panel_for_entity(this, this.selected_entity_profile_id);
  },
  async submit_case_form_for_risk() {
    if (!this.can_submit_case_form) {
      this.case_form_submission_error = this.missing_required_case_form_facts.length
        ? 'Complete all required case details before submitting the form.'
        : 'Save the section you are editing before submitting the form.';
      return;
    }

    this.submitting_case_form = true;
    this.case_form_submission_error = '';
    this.case_form_submission_success = '';

    try {
      await this.resolve_case_backend_ids();
      if (!this.selected_entity_profile_id) {
        throw new Error('This case has no entity profile available for risk scoring.');
      }

      const assessments = await this.resolve_risk_assessments();
      if (!assessments.length) {
        throw new Error('No risk assessment is configured for this case type.');
      }

      const submissions = risk_submissions(assessments, this.case_form_facts);
      if (!submissions.length) {
        throw new Error('None of the completed form answers match the configured risk questions.');
      }

      await Promise.all(submissions.map((submission) => submit_risk_assessment_answers({
        entity_id: this.selected_entity_profile_id,
        assessment_id: submission.assessment_id,
        answers: submission.answers,
      })));
      await this.load_risk_panel();
      this.case_form_submission_success = 'Form submitted and risk calculated successfully.';
    } catch (error) {
      this.case_form_submission_error = error_message(error, 'Failed to submit the form for risk scoring.');
    } finally {
      this.submitting_case_form = false;
    }
  },
};

function risk_submissions(assessments = [], facts = []) {
  return assessments
    .map((assessment) => ({
      assessment_id: to_number(assessment?.id),
      answers: risk_answers_for_assessment(assessment, facts),
    }))
    .filter((submission) => submission.assessment_id && Object.keys(submission.answers).length);
}

function risk_answers_for_assessment(assessment = {}, facts = []) {
  return Object.fromEntries(
    to_array(assessment?.questions)
      .map((question) => risk_answer_entry(question, facts))
      .filter(Boolean)
  );
}

function risk_answer_entry(question = {}, facts = []) {
  const question_id = to_number(question?.id);
  const fact = facts.find((item) => (
    Number(item?.question_id) === Number(question_id)
    || normalize_label(item?.label) === normalize_label(question?.text || question?.question_text)
  ));
  const matched_values = fact_answer_values(fact)
    .map((value) => matching_risk_choice(value, question?.choices))
    .filter(Boolean)
    .map(risk_choice_label)
    .filter(Boolean);

  if (!question_id || !matched_values.length) return null;
  return [String(question_id), matched_values.length === 1 ? matched_values[0] : matched_values];
}

function fact_answer_values(fact = {}) {
  if (Array.isArray(fact?.values) && fact.values.length) return fact.values;
  return normalize_text(fact?.value).split(',').map(normalize_text).filter(Boolean);
}

function matching_risk_choice(value, choices = []) {
  const normalized_value = normalize_label(value);
  return to_array(choices).find((choice) => normalize_label(risk_choice_label(choice)) === normalized_value);
}

function empty_payload() {
  return {};
}

function case_backend_ids_are_resolved(component) {
  return component.selected_entity_profile_id && component.selected_entity_type_id;
}

function assessment_with_questions(assessment = {}, questions = []) {
  return {
    ...assessment,
    questions,
  };
}

async function resolve_missing_backend_ids(component) {
  if (!component.selected_application_id) {
    return;
  }

  await load_backend_ids(component);
}

async function load_backend_ids(component) {
  try {
    const application_forms = await fetch_requestor_application_forms(component.selected_application_id);
    set_backend_ids_from_forms(component, application_forms);
  } catch {
    clear_missing_backend_ids(component);
  }
}

export function set_backend_ids_from_forms(component, application_forms = []) {
  component.resolved_entity_profile_id = component.resolved_entity_profile_id
    || entity_profile_id_from_application_forms(application_forms);
  component.resolved_entity_type_id = component.resolved_entity_type_id
    || entity_type_id_from_application_forms(application_forms);
}

function clear_missing_backend_ids(component) {
  component.resolved_entity_profile_id = component.resolved_entity_profile_id || null;
  component.resolved_entity_type_id = component.resolved_entity_type_id || null;
}

async function risk_assessments_for_context(component, assessment_id) {
  if (assessment_id && !component.selected_entity_type_id) {
    return [{ id: assessment_id }];
  }

  return configured_or_empty_risk_assessments(component, assessment_id);
}

async function configured_or_empty_risk_assessments(component, assessment_id) {
  if (!component.selected_entity_type_id) {
    return [];
  }

  const assessments = await configured_risk_assessments(component, component.selected_entity_type_id);
  return selected_assessments(assessments, assessment_id);
}

async function configured_risk_assessments(component, entity_type_id) {
  try {
    const config_data = await getXVerdictFullConfigByEntityType({
      entity_type_id,
      project_id: component?.project_id || null,
    });
    return mapRiskBindingsToAssessments(config_data);
  } catch {
    return [];
  }
}

function selected_assessments(assessments = [], assessment_id) {
  return assessment_id
    ? assessments.filter((assessment) => Number(assessment?.id) === Number(assessment_id))
    : assessments;
}

function reset_risk_panel(component) {
  component.loading_risk_panel = false;
  component.risk_panel = null;
  component.risk_panel_message = '';
}

async function load_risk_panel_for_entity(component, entity_id) {
  component.loading_risk_panel = true;
  try {
    const assessments = await component.resolve_risk_assessments();
    await apply_first_risk_panel(component, entity_id, assessments);
  } catch (error) {
    component.risk_panel = null;
    component.risk_panel_message = error_message(error, 'Failed to load risk score.');
  } finally {
    component.loading_risk_panel = false;
  }
}

async function apply_first_risk_panel(component, entity_id, assessments = []) {
  if (!assessments.length) {
    component.risk_panel_message = 'No risk assessment is bound or selected for this case.';
    return;
  }

  const result = await first_submitted_risk_panel(entity_id, assessments);
  component.risk_panel = result.panel;
  component.risk_panel_message = result.panel
    ? ''
    : error_message(result.error, 'No backend risk score has been submitted for this case yet.');
}

async function first_submitted_risk_panel(entity_id, assessments = []) {
  let last_error = null;
  for (const assessment of assessments) {
    const result = await submitted_risk_panel(entity_id, assessment);
    if (result.panel) {
      return result;
    }
    last_error = result.error;
  }
  return { panel: null, error: last_error };
}

async function submitted_risk_panel(entity_id, assessment = {}) {
  const assessment_id = to_number(assessment?.id);
  if (!assessment_id) {
    return { panel: null, error: null };
  }

  try {
    const response = await fetch_submitted_risk_assessment_answers({ entity_id, assessment_id });
    return { panel: normalize_risk_panel(response?.data || response, assessment), error: null };
  } catch (error) {
    return { panel: null, error };
  }
}
